/**
 * L1J Web Client - MapCollision
 *
 * 게이트웨이의 맵 통행 가능 여부 HTTP 엔드포인트(/map/{id})에서 비트셋을 받아와
 * 캐싱하고, 클릭 이동이 벽/장애물 타일을 통과하지 못하도록 조회하는 기능을 제공한다.
 *
 * 응답 바이너리 포맷 (little-endian):
 *   originX(4) + originY(4) + width(4) + height(4) + bitset(ceil(width*height/8))
 * 비트셋 인덱스 = (worldY - originY) * width + (worldX - originX), 1=통행가능.
 *
 * 서버 원본(L1V1Map#isPassable)의 문 상태·코너 커팅까지는 반영하지 않은
 * 단순화된 근사치다 (게이트웨이 MapPassabilityService 참고).
 */

import { ServerConfig } from '../config';
import { HEADING_VECTORS } from '../utils/isometric';

interface MapData {
  originX: number;
  originY: number;
  width: number;
  height: number;
  bitset: Uint8Array;
}

/** 경로 탐색 결과의 한 지점 (서버 타일 좌표). */
export interface PathNode {
  tx: number;
  ty: number;
}

export interface FindPathOptions {
  /** 탐색할 최대 노드 수. 초과하면 지금까지 목표에 가장 가까운 노드로 경로를 만든다. */
  maxNodes?: number;
  /** true면 목표에 도달 못 해도 가장 가까운 지점까지의 경로를 돌려준다 (기본 true). */
  allowPartial?: boolean;
  /**
   * 통행 비트셋과 별개로 "지금 막혀 있는" 타일을 알려주는 판정 (서버 타일 좌표).
   *
   * 비트셋에는 지형만 들어 있어서 그 자리에 서 있는 NPC/몹/다른 플레이어는 보이지
   * 않는다. 이것을 반영하지 않으면 마을처럼 NPC가 촘촘한 곳에서 A*가 NPC를 관통하는
   * 경로를 내고, 첫 걸음이 곧바로 막혀 이동 자체가 취소된다.
   *
   * 출발 타일에는 적용하지 않는다 — 자기 발밑이 막혔다고 판정되면 한 걸음도 못 뗀다.
   */
  isBlocked?: (tx: number, ty: number) => boolean;
}

/** 직교 이동 비용 10, 대각 이동 비용 14 (√2 ≒ 1.4 근사). */
const COST_ORTHOGONAL = 10;
const COST_DIAGONAL = 14;

/**
 * 노드 예산 기본값 — 한 번의 클릭에서 확장(pop)할 최대 타일 수.
 *
 * 근거:
 * - 실제 클릭은 화면 안(서버 가시 범위 = 맨해튼 19타일)에서 일어나므로, 벽을 크게
 *   우회하더라도 반경 40타일 남짓의 영역(원 면적 ≒ 5,000타일)이면 충분하다.
 * - 맵은 작아도 384x384(맵 2005), 필드 맵 4는 1856x1536 = 285만 타일이다. 도달 불가
 *   목표를 클릭하면 A*는 열린 목록이 마를 때까지 맵 전체를 훑으므로 상한이 필수다.
 * - 실측(node, 400x400 격자): 20~60타일 클릭 0.03~0.25ms, 예산을 전부 소진하는 도달 불가
 *   클릭 6~8ms로 한 프레임(16.7ms) 안에 들어온다. 예산을 20,000으로 올리면 최악이 25ms가
 *   되어 프레임을 흘린다.
 *
 * 대가: 수백 타일을 우회해야 하는 먼 목표는 예산이 먼저 끊겨 벽 앞까지만 가는 부분 경로가
 * 나온다. 이때는 도착 후 다시 클릭하면 그 지점에서 이어서 탐색한다.
 */
const DEFAULT_MAX_NODES = 8000;

/**
 * A*의 열린 목록용 최소 힙 (키 = f 값, 값 = 타일 인덱스).
 * 배열 선형 탐색은 노드가 수천 개일 때 클릭 한 번에 수십 ms를 먹으므로 O(log n) 힙을 쓴다.
 */
class MinHeap {
  private keys: number[] = [];
  private values: number[] = [];

  get size(): number {
    return this.values.length;
  }

  push(key: number, value: number): void {
    let i = this.values.length;
    this.keys.push(key);
    this.values.push(value);
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.keys[parent] <= this.keys[i]) break;
      this.swap(parent, i);
      i = parent;
    }
  }

  /** 최소 키의 값을 꺼낸다. 비어 있으면 호출하지 말 것 (size로 먼저 확인). */
  pop(): number {
    const top = this.values[0];
    const lastKey = this.keys.pop() as number;
    const lastValue = this.values.pop() as number;
    const n = this.values.length;
    if (n > 0) {
      this.keys[0] = lastKey;
      this.values[0] = lastValue;
      let i = 0;
      for (;;) {
        const left = i * 2 + 1;
        const right = left + 1;
        let smallest = i;
        if (left < n && this.keys[left] < this.keys[smallest]) smallest = left;
        if (right < n && this.keys[right] < this.keys[smallest]) smallest = right;
        if (smallest === i) break;
        this.swap(smallest, i);
        i = smallest;
      }
    }
    return top;
  }

  private swap(a: number, b: number): void {
    const k = this.keys[a];
    this.keys[a] = this.keys[b];
    this.keys[b] = k;
    const v = this.values[a];
    this.values[a] = this.values[b];
    this.values[b] = v;
  }
}

/**
 * 옥타일 거리 휴리스틱. 대각로 min(dx,dy)칸을 가고 나머지는 직진하는 최단 비용이므로
 * 실제 비용을 절대 넘지 않는다 (넘으면 A*가 최적 경로를 놓친다).
 */
function octileDistance(dx: number, dy: number): number {
  const ax = dx < 0 ? -dx : dx;
  const ay = dy < 0 ? -dy : dy;
  const min = ax < ay ? ax : ay;
  const max = ax < ay ? ay : ax;
  return COST_DIAGONAL * min + COST_ORTHOGONAL * (max - min);
}

class MapCollision {
  private cache: Map<number, MapData> = new Map();
  private loading: Map<number, Promise<MapData | null>> = new Map();
  private currentMapId: number | null = null;

  /**
   * 문 타일의 통행 여부 덮어쓰기 (`"mapId:x:y"` → passable).
   * 정적 통행 비트셋에는 없는 런타임 상태다. setDoorPassable 참조.
   */
  private doorOverrides: Map<string, boolean> = new Map();

  /**
   * 현재 맵을 설정하고, 아직 로드하지 않았다면 백그라운드로 미리 불러온다.
   * @param onLoaded 데이터를 실제로 사용할 수 있게 됐을 때 호출된다 (이미
   *   캐시돼 있으면 동기적으로, 아니면 fetch 완료 후 비동기로). 타일
   *   렌더링처럼 데이터 도착 시점에 다시 그려야 하는 화면에서 사용.
   */
  setCurrentMap(mapId: number, onLoaded?: () => void): void {
    if (this.currentMapId === mapId) {
      if (!onLoaded) return;
      if (this.cache.has(mapId)) {
        onLoaded();
        return;
      }
      // 같은 mapId로 로드가 진행 중인 동안 재호출된 경우 —
      // 진행 중인 promise에 콜백을 연결해 onLoaded가 유실되지 않게 한다.
      const pending = this.loading.get(mapId);
      if (pending) {
        pending
          .then((data) => {
            if (data) onLoaded();
          })
          .catch((err) => {
            console.warn('[MapCollision] Failed to load map', mapId, err);
          });
      }
      return;
    }
    this.currentMapId = mapId;
    this.ensureLoaded(mapId)
      .then((data) => {
        if (data && onLoaded) onLoaded();
      })
      .catch((err) => {
        console.warn('[MapCollision] Failed to preload map', mapId, err);
      });
  }

  /**
   * 지정한 좌표가 통행 가능한지 반환한다. 맵 데이터가 아직 로드되지 않았다면
   * (첫 조회 시점) 안전하게 true(통행 가능)를 반환한다 — 잘못된 벽 데이터로
   * 캐릭터가 아예 못 움직이게 되는 것보다, 로드 전까지는 막지 않는 쪽을 택함.
   */
  isPassable(mapId: number, worldX: number, worldY: number): boolean {
    // 문(S_Door)은 정적 통행 데이터에 없는 런타임 상태다 — 열리고 닫힐 때마다
    // 같은 타일의 통행 여부가 뒤집히므로 비트셋보다 먼저 본다.
    const override = this.doorOverrides.get(
      MapCollision.doorKey(mapId, Math.floor(worldX), Math.floor(worldY))
    );
    if (override !== undefined) return override;

    const data = this.cache.get(mapId);
    if (!data) return true;

    const x = Math.floor(worldX) - data.originX;
    const y = Math.floor(worldY) - data.originY;
    if (x < 0 || y < 0 || x >= data.width || y >= data.height) return true;

    const bitIndex = y * data.width + x;
    const byte = data.bitset[bitIndex >> 3];
    return (byte & (1 << (bitIndex & 7))) !== 0;
  }

  /**
   * 문 타일의 통행 가능 여부를 덮어쓴다 (`S_Door`, opcode 35).
   *
   * 서버는 문이 열리거나 닫힐 때마다 이 패킷을 보내는데, 통행 비트셋은 맵을
   * 처음 받을 때의 정적 스냅샷이라 그 변화를 담지 못한다. 그래서 좌표별
   * 덮어쓰기 표를 따로 두고 `isPassable`이 이 표를 먼저 보게 한다.
   *
   * 맵을 옮기면 이전 맵의 문 상태는 의미가 없지만, 키에 mapId가 들어 있어
   * 서로 섞이지 않는다 — 되돌아왔을 때 마지막 상태를 그대로 쓴다.
   */
  setDoorPassable(mapId: number, worldX: number, worldY: number, passable: boolean): void {
    this.doorOverrides.set(
      MapCollision.doorKey(mapId, Math.floor(worldX), Math.floor(worldY)),
      passable
    );
  }

  /** 문 덮어쓰기 표의 키. 좌표는 0~65535 범위라 16비트씩 접어 넣는다. */
  private static doorKey(mapId: number, x: number, y: number): string {
    return `${mapId}:${x}:${y}`;
  }

  isLoaded(mapId: number): boolean {
    return this.cache.has(mapId);
  }

  /**
   * 8방향 A* 경로 탐색. 벽을 우회해 목표까지 가는 타일 경로를 만든다.
   *
   * 반환: 출발 타일을 **제외한** 경유 타일 배열 (마지막이 도착지).
   *       갈 수 없거나 이미 목표에 서 있으면 빈 배열([]).
   *
   * 규칙:
   * - 대각선 코너 컷 금지. (dx,dy)가 둘 다 0이 아닌 이동은 인접한 두 직교 타일
   *   (x+dx, y) / (x, y+dy)이 **둘 다 통행 가능할 때만** 허용한다. 서버
   *   L1V1Map#isPassable도 대각 이동을 "직교 2스텝"으로 분해해 검사하므로
   *   (isPassableUpThenRight 등), 이 조건을 빼면 서버가 거부하는 경로가 나와
   *   캐릭터가 모서리에서 멈춘다.
   * - 맵 데이터 미로드면 빈 배열. isPassable은 미로드 시 true를 돌려주므로 그대로
   *   탐색하면 벽을 무시한 엉터리 경로가 된다 — 호출자가 폴백하도록 둔다.
   * - options.isBlocked를 주면 지형 외에 그 타일도 벽으로 본다 (그 자리에 서 있는
   *   NPC/몹 우회용). 목표 타일이 막혀 있으면 allowPartial로 그 옆까지 간다.
   * - 목표가 벽이거나 도달 불가여도 allowPartial(기본 true)이면 가장 가까이 접근한
   *   지점까지의 경로를 돌려준다 (벽을 클릭했을 때 벽 앞까지는 걸어가도록).
   */
  findPath(
    mapId: number,
    startX: number,
    startY: number,
    goalX: number,
    goalY: number,
    options?: FindPathOptions,
  ): PathNode[] {
    const data = this.cache.get(mapId);
    if (!data) return [];

    const { originX, originY, width, height, bitset } = data;
    const maxNodes = options?.maxNodes !== undefined && options.maxNodes > 0
      ? options.maxNodes
      : DEFAULT_MAX_NODES;
    const allowPartial = options?.allowPartial !== false;

    // 좌표는 맵 원점 기준 로컬 좌표로 다루고, 타일 키는 y * width + x 정수로 만든다
    // (문자열 키 생성을 피하기 위함).
    const sx = Math.floor(startX) - originX;
    const sy = Math.floor(startY) - originY;
    if (sx < 0 || sy < 0 || sx >= width || sy >= height) return [];

    // 목표가 맵 밖이면 경계 안으로 당겨 붙인다 (그 방향으로 최대한 걸어가게).
    let gx = Math.floor(goalX) - originX;
    let gy = Math.floor(goalY) - originY;
    if (gx < 0) gx = 0; else if (gx >= width) gx = width - 1;
    if (gy < 0) gy = 0; else if (gy >= height) gy = height - 1;

    if (sx === gx && sy === gy) return [];

    // 맵 밖은 벽으로 취급한다 (isPassable은 경계 밖에 true를 돌려주므로 여기서 별도 판정).
    const isBlocked = options?.isBlocked;
    const passable = (x: number, y: number): boolean => {
      if (x < 0 || y < 0 || x >= width || y >= height) return false;
      const bitIndex = y * width + x;
      if ((bitset[bitIndex >> 3] & (1 << (bitIndex & 7))) === 0) return false;
      // 출발 타일은 자기 자신이 서 있는 곳이므로 점유 판정에서 제외한다.
      if (isBlocked !== undefined && (x !== sx || y !== sy)) {
        if (isBlocked(x + originX, y + originY)) return false;
      }
      return true;
    };

    const startIndex = sy * width + sx;
    const goalIndex = gy * width + gx;

    const open = new MinHeap();
    const gScore = new Map<number, number>();
    const cameFrom = new Map<number, number>();
    const closed = new Set<number>();

    const startH = octileDistance(gx - sx, gy - sy);
    gScore.set(startIndex, 0);
    open.push(startH, startIndex);

    // 예산 초과·도달 불가일 때 돌려줄 "가장 목표에 가까운 노드".
    // 휴리스틱이 같으면 더 싸게 도달한 쪽을 택한다.
    let bestIndex = startIndex;
    let bestH = startH;
    let bestG = 0;

    let expanded = 0;
    let found = false;

    while (open.size > 0) {
      const current = open.pop();
      if (closed.has(current)) continue; // 더 나은 경로로 이미 확장된 노드
      closed.add(current);

      if (current === goalIndex) {
        found = true;
        break;
      }

      // 노드 예산 초과 — 브라우저 메인 스레드가 멈추지 않도록 중단한다.
      if (++expanded > maxNodes) break;

      const cx = current % width;
      const cy = (current - cx) / width;
      const cg = gScore.get(current) as number;

      for (const { dx, dy } of HEADING_VECTORS) {
        const nx = cx + dx;
        const ny = cy + dy;
        if (!passable(nx, ny)) continue;
        // 코너 컷 금지: 대각은 인접 직교 두 타일이 모두 열려 있어야 한다.
        if (dx !== 0 && dy !== 0) {
          if (!passable(cx + dx, cy) || !passable(cx, cy + dy)) continue;
        }

        const next = ny * width + nx;
        if (closed.has(next)) continue;

        const ng = cg + (dx !== 0 && dy !== 0 ? COST_DIAGONAL : COST_ORTHOGONAL);
        const known = gScore.get(next);
        if (known !== undefined && known <= ng) continue;

        gScore.set(next, ng);
        cameFrom.set(next, current);

        const h = octileDistance(gx - nx, gy - ny);
        open.push(ng + h, next);

        if (h < bestH || (h === bestH && ng < bestG)) {
          bestIndex = next;
          bestH = h;
          bestG = ng;
        }
      }
    }

    let endIndex: number;
    if (found) {
      endIndex = goalIndex;
    } else if (allowPartial) {
      endIndex = bestIndex;
    } else {
      return [];
    }
    if (endIndex === startIndex) return [];

    // cameFrom을 거꾸로 따라가며 경로 복원 (출발 타일은 포함하지 않는다).
    const path: PathNode[] = [];
    let cur = endIndex;
    while (cur !== startIndex) {
      const lx = cur % width;
      const ly = (cur - lx) / width;
      path.push({ tx: lx + originX, ty: ly + originY });
      const prev = cameFrom.get(cur);
      if (prev === undefined) break; // 이론상 없는 경우 — 무한 루프 방어
      cur = prev;
    }
    path.reverse();
    return path;
  }

  /** 맵의 원점/크기를 반환한다 (로드 전이면 null). 화면에 맵 경계를 그릴 때 사용. */
  getMapBounds(mapId: number): { originX: number; originY: number; width: number; height: number } | null {
    const data = this.cache.get(mapId);
    if (!data) return null;
    return { originX: data.originX, originY: data.originY, width: data.width, height: data.height };
  }

  private async ensureLoaded(mapId: number): Promise<MapData | null> {
    const cached = this.cache.get(mapId);
    if (cached) return cached;

    const pending = this.loading.get(mapId);
    if (pending) return pending;

    const promise = this.fetchMap(mapId);
    this.loading.set(mapId, promise);
    try {
      const result = await promise;
      if (result) this.cache.set(mapId, result);
      return result;
    } finally {
      // fetch 실패(reject) 시에도 반드시 제거 — rejected promise가 loading에
      // 영구 잔존하면 이후 재시도가 불가능해진다.
      this.loading.delete(mapId);
    }
  }

  private async fetchMap(mapId: number): Promise<MapData | null> {
    const url = `${ServerConfig.MAP_HTTP_URL}/map/${mapId}`;
    const res = await fetch(url);
    if (!res.ok) {
      console.warn(`[MapCollision] Map ${mapId} not available (${res.status})`);
      return null;
    }
    const buf = await res.arrayBuffer();
    if (buf.byteLength < 16) {
      console.warn(`[MapCollision] Map ${mapId} response too short (${buf.byteLength} bytes) — not cached`);
      return null;
    }
    const view = new DataView(buf);
    const originX = view.getInt32(0, true);
    const originY = view.getInt32(4, true);
    const width = view.getInt32(8, true);
    const height = view.getInt32(12, true);
    const bitset = new Uint8Array(buf, 16);

    // 무결성 검증: bitset 길이는 ceil(width*height/8) 이어야 한다.
    // 다르면 잘린/손상된 응답이므로 캐시하지 않는다 (isPassable이 잘못된
    // 벽 판정을 돌려주는 것 방지).
    const expectedBytes = width > 0 && height > 0 ? Math.ceil((width * height) / 8) : -1;
    if (expectedBytes < 0 || bitset.length !== expectedBytes) {
      console.warn(
        `[MapCollision] Map ${mapId} bitset length mismatch: got ${bitset.length}, ` +
        `expected ${expectedBytes} (${width}x${height}) — not cached`,
      );
      return null;
    }

    console.log(`[MapCollision] Loaded map ${mapId}: ${width}x${height} @ (${originX},${originY})`);
    return { originX, originY, width, height, bitset };
  }
}

export const mapCollision = new MapCollision();
export default mapCollision;
